import { useState, useEffect, useCallback, useRef } from 'react';
import { useSetAchievementDeliveredMutation } from '@/store/api';
import { REFERRAL_ACHIEVEMENT_TYPES } from '@/constants';
import { setLatestAchievementType } from '@/store/slices/achievements.slice';
import { useAppDispatch } from '@/store';
import type { Achievement } from '@/types/layout.types';

interface UseAchievementsReturn {
	showMilestoneModal: boolean;
	pendingAchievement: Achievement | null;
	handleMilestoneClose: () => Promise<void>;
}

export function useAchievements(
	userAchievements: Achievement[] | undefined
): UseAchievementsReturn {
	const dispatch = useAppDispatch();
	const [showMilestoneModal, setShowMilestoneModal] = useState(false);
	const [pendingAchievement, setPendingAchievement] = useState<Achievement | null>(null);
	const [achievementQueue, setAchievementQueue] = useState<Achievement[]>([]);
	const [setAchievementDelivered] = useSetAchievementDeliveredMutation();
	const queuedAchievementIdsRef = useRef(new Set<string>());

	useEffect(() => {
		if (!userAchievements || userAchievements.length === 0) return;

		const leaderboardAchievements = userAchievements.filter((a) =>
			REFERRAL_ACHIEVEMENT_TYPES.includes(a.type)
		);

		if (leaderboardAchievements.length > 0) {
			const latestLeaderboardAchievement = leaderboardAchievements.reduce((latest, current) => {
				return new Date(current.created_date) > new Date(latest.created_date) ? current : latest;
			});

			dispatch(setLatestAchievementType(latestLeaderboardAchievement.type));
		}

		const undeliveredAchievements = userAchievements
			.filter((achievement) => !achievement.is_delivered)
			.sort((a, b) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime());

		const newAchievements = undeliveredAchievements.filter((achievement) => {
			if (queuedAchievementIdsRef.current.has(achievement.id)) return false;

			queuedAchievementIdsRef.current.add(achievement.id);
			return true;
		});

		if (!newAchievements.length) return;

		setAchievementQueue((prev) => [...prev, ...newAchievements]);
	}, [userAchievements, dispatch]);

	useEffect(() => {
		if (pendingAchievement || achievementQueue.length === 0) return;

		const [nextAchievement, ...remainingAchievements] = achievementQueue;

		setPendingAchievement(nextAchievement);
		setAchievementQueue(remainingAchievements);
		setShowMilestoneModal(true);
	}, [achievementQueue, pendingAchievement]);

	const handleMilestoneClose = useCallback(async () => {
		const achievementToMark = pendingAchievement;

		setShowMilestoneModal(false);
		setPendingAchievement(null);

		if (achievementToMark) {
			try {
				await setAchievementDelivered(achievementToMark.id).unwrap();
			} catch (e) {
				console.warn('Failed to mark achievements delivered', e);
			}
		}
	}, [pendingAchievement, setAchievementDelivered]);

	return {
		showMilestoneModal,
		pendingAchievement,
		handleMilestoneClose,
	};
}
